/**
 * Maps Node.js process.platform values to Mixpanel's expected OS format
 * @param platform - Node.js platform string (win32, darwin, linux, etc.)
 * @returns Mixpanel-compatible OS name (Windows, Mac OS X, Linux, etc.)
 */
export function mapPlatformToMixpanel(platform) {
    switch (platform) {
        case "win32":
            return "Windows";
        case "darwin":
            return "Mac OS X";
        case "linux":
            return "Linux";
        default:
            return platform;
    }
}
/**
 * APP EVENTS
 */
/**
 * App launched event - sent when Vortex starts up
 * @param os Operating system (Node.js platform string: win32, darwin, linux)
 * @param os_version Operating system version (e.g., "10.0.22000" for Windows 11)
 * @param architecture CPU architecture (e.g., "x64", "arm64")
 */
export class AppLaunchedEvent {
    eventName = "app_launched";
    properties;
    constructor(os, os_version, architecture) {
        this.properties = {
            $os: mapPlatformToMixpanel(os), // Override auto-detected OS for accuracy
            $os_version: os_version, // Not auto-tracked by mixpanel-browser
            architecture, // Custom property for CPU architecture
        };
    }
}
/**
 * DNU - NEEDS TO BE FIRED BEFORE ANALYTICS ARE INITIALIZED
 * Event sent when the application is updated.
 * @param from_version Previous version
 * @param to_version New version
 * @param os Operating system (Node.js platform string: win32, darwin, linux)
 */
export class AppUpdatedEvent {
    eventName = "app_updated";
    properties;
    constructor(from_version, to_version, os) {
        this.properties = {
            from_version,
            to_version,
            $os: mapPlatformToMixpanel(os),
        };
    }
}
/**
 * Event sent when an upsell prompt is clicked in the application.
 */
export class AppUpsellClickedEvent {
    eventName = "app_upsell_clicked";
    properties = {};
    constructor() { }
}
/**
 * COLLECTION EVENTS
 */
/* COLLECTION DRAFTING AND UPLOADING */
/**
 * Event sent when a collection draft is created in Vortex.
 * @param collection_name Name of the collection
 * @param game_name Name of the game
 * @param creation_method How the collection was created
 */
export class CollectionsDraftedEvent {
    eventName = "collection_drafted";
    properties;
    constructor(collection_name, game_name, creation_method) {
        this.properties = {
            collection_name,
            game_name,
            creation_method,
        };
    }
}
/**
 * Event sent when a new draft collection is uploaded.
 * @param collection_name Name of the collection
 * @param game_name Name of the game
 */
export class CollectionsDraftUploadedEvent {
    eventName = "collection_draft_uploaded";
    properties;
    constructor(collection_name, game_name) {
        this.properties = {
            collection_name,
            game_name,
        };
    }
}
/**
 * Event sent when a draft collection update is uploaded.
 * @param collection_name Name of the collection
 * @param game_name Name of the game
 */
export class CollectionsDraftUpdateUploadedEvent {
    eventName = "collection_draft_updated";
    properties;
    constructor(collection_name, game_name) {
        this.properties = {
            collection_name,
            game_name,
        };
    }
}
/* COLLECTION DOWNLOAD */
/**
 * Event sent when a collection download is clicked/initiated by the user.
 * @param collection_slug Slug of the collection
 * @param game_id ID of the game
 */
export class CollectionsDownloadClickedEvent {
    eventName = "collections_download_clicked";
    properties;
    constructor(collection_slug, game_id) {
        this.properties = { collection_slug, game_id };
    }
}
/**
 * Event sent when a collection download is completed.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 * @param mod_count Number of mods in the collection
 * @param duration_ms Duration in milliseconds
 */
export class CollectionsDownloadCompletedEvent {
    eventName = "collections_download_completed";
    properties;
    constructor(collection_id, revision_id, game_id, file_size, duration_ms) {
        this.properties = {
            collection_id,
            revision_id,
            game_id,
            file_size,
            duration_ms,
        };
    }
}
/**
 * Event sent when a collection download fails.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 * @param error_code Error code
 * @param error_message Error message
 */
export class CollectionsDownloadFailedEvent {
    eventName = "collections_download_failed";
    properties;
    constructor(collection_id, revision_id, game_id, error_code, error_message) {
        this.properties = {
            collection_id,
            revision_id,
            game_id,
            error_code,
            error_message,
        };
    }
}
/**
 * Event sent when a collection download is cancelled.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 */
export class CollectionsDownloadCancelledEvent {
    eventName = "collections_download_cancelled";
    properties;
    constructor(collection_id, revision_id, game_id) {
        this.properties = { collection_id, revision_id, game_id };
    }
}
/* COLLECTION INSTALLATION */
/** *
 * Event sent when a collection installation is started.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 * @param mod_count Number of mods in the collection
 */
export class CollectionsInstallationStartedEvent {
    eventName = "collections_installation_started";
    properties;
    constructor(collection_id, revision_id, game_id, mod_count) {
        this.properties = { collection_id, revision_id, game_id, mod_count };
    }
}
/**
 * Event sent when a collection installation is completed.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 * @param mod_count Number of mods in the collection
 * @param duration_ms Duration in milliseconds
 */
export class CollectionsInstallationCompletedEvent {
    eventName = "collections_installation_completed";
    properties;
    constructor(collection_id, revision_id, game_id, mod_count, duration_ms) {
        this.properties = {
            collection_id,
            revision_id,
            game_id,
            mod_count,
            duration_ms,
        };
    }
}
/**
 * Event sent when a collection installation fails.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 * @param error_code Error code
 * @param error_message Error message
 */
export class CollectionsInstallationFailedEvent {
    eventName = "collections_installation_failed";
    properties;
    constructor(collection_id, revision_id, game_id, error_code, error_message) {
        this.properties = {
            collection_id,
            revision_id,
            game_id,
            error_code,
            error_message,
        };
    }
}
/**
 * Event sent when a collection installation is cancelled.
 * @param collection_id ID of the collection
 * @param revision_id ID of the revision
 * @param game_id ID of the game
 */
export class CollectionsInstallationCancelledEvent {
    eventName = "collections_installation_cancelled";
    properties;
    constructor(collection_id, revision_id, game_id) {
        this.properties = { collection_id, revision_id, game_id };
    }
}
/**
 * MOD EVENTS
 */
/**
 * Event sent when a mod download is started from the client.
 * This client-side event complements the server-side mods_download_started event
 * to enable success rate calculations by matching with _completed or _failed events.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 * @note "mods_download_started" event name is taken by server-side. Use "mods_download_started_client" instead.
 */
export class ModsDownloadStartedClientEvent {
    eventName = "mods_download_started_client";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid) {
        this.properties = { mod_id, file_id, game_id, mod_uid, file_uid };
    }
}
/** DONE
 * Event sent when a mod download is completed.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 * @param file_size Size of the file
 * @param duration_ms Duration in milliseconds
 */
export class ModsDownloadCompletedEvent {
    eventName = "mods_download_completed";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid, file_size, duration_ms) {
        this.properties = {
            mod_id,
            file_id,
            game_id,
            mod_uid,
            file_uid,
            file_size,
            duration_ms,
        };
    }
}
/**
 * Event sent when mod download is cancelled.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 */
export class ModsDownloadCancelledEvent {
    eventName = "mods_download_cancelled";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid) {
        this.properties = { mod_id, file_id, game_id, mod_uid, file_uid };
    }
}
/** DONE
 * Event sent when a mod download fails.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 * @param error_code Error code
 * @param error_message Error message
 */
export class ModsDownloadFailedEvent {
    eventName = "mods_download_failed";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid, error_code, error_message) {
        this.properties = {
            mod_id,
            file_id,
            game_id,
            mod_uid,
            file_uid,
            error_code,
            error_message,
        };
    }
}
/** DONE
 * Event sent when mod installation is started. Not sent for collection bundle/manifest mod.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 */
export class ModsInstallationStartedEvent {
    eventName = "mods_installation_started";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid) {
        this.properties = { mod_id, file_id, game_id, mod_uid, file_uid };
    }
}
/** DONE
 * Event sent when mod installation is completed. Not sent for collection bundle/manifest mod.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 * @param duration_ms Duration in milliseconds
 */
export class ModsInstallationCompletedEvent {
    eventName = "mods_installation_completed";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid, duration_ms) {
        this.properties = {
            mod_id,
            file_id,
            game_id,
            mod_uid,
            file_uid,
            duration_ms,
        };
    }
}
/**
 * Event sent when mod installation is cancelled. Not sent for collection bundle/manifest mod.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 */
export class ModsInstallationCancelledEvent {
    eventName = "mods_installation_cancelled";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid) {
        this.properties = { mod_id, file_id, game_id, mod_uid, file_uid };
    }
}
/** DONE
 * Event sent when mod installation fails. Not sent for collection bundle/manifest mod.
 * @param mod_id ID of the mod
 * @param file_id ID of the file
 * @param game_id ID of the game
 * @param mod_uid UID of the mod
 * @param file_uid UID of the file
 * @param error_code Error code
 * @param error_message Error message
 */
export class ModsInstallationFailedEvent {
    eventName = "mods_installation_failed";
    properties;
    constructor(mod_id, file_id, game_id, mod_uid, file_uid, error_code, error_message) {
        this.properties = {
            mod_id,
            file_id,
            game_id,
            mod_uid,
            file_uid,
            error_code,
            error_message,
        };
    }
}
/**
 * HEALTH CHECK EVENTS
 */
/**
 * Event sent when a user provides feedback on a health check requirement.
 * @param feedback_type Whether the feedback was positive or negative
 * @param game_id Game domain ID
 * @param mod_id Nexus mod ID of the missing requirement
 * @param required_by_mod_id Nexus mod ID of the mod that requires the dependency
 * @param feedback_reasons Array of reason keys (only for negative feedback)
 */
export class HealthCheckFeedbackEvent {
    eventName = "health_check_feedback";
    properties;
    constructor(feedback_type, game_id, mod_id, required_by_mod_id, feedback_reasons) {
        this.properties = {
            feedback_type,
            game_id,
            mod_id,
            required_by_mod_id,
            ...(feedback_reasons?.length ? { feedback_reasons } : {}),
        };
    }
}
